import { Add as AddIcon, ArrowDownward, ArrowUpward, DeleteOutline } from '@mui/icons-material';
import { Box, Button, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProfileCardDraft, ProfileDraft, emptyFeatureCard, emptyServiceCard } from '../../../library/models/tenantProfile';
import ResourcesIds from '../../../resources/ResourcesIds';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { addButtonStyles, blockCardStyles, blockTitleStyles, cardHeaderStyles, cardLabelStyles, cardRowStyles, editorWrapperStyles, emptyBlockStyles, fieldPairStyles, iconChoiceStyles, iconPickerStyles, rowActionsStyles, textFieldStyles } from './aboutEditor.styles';
import { PROFILE_ICON_NAMES, profileIconTile } from './profileIcons';

interface IAboutEditorProps {
    draft: ProfileDraft;
    /**
     * Card keys the API would reject. Passed in rather than computed here so the page can
     * use the same answer to decide whether Save is available -- one rule, one evaluation.
     */
    invalidKeys: Set<string>;
    onChange: (draft: ProfileDraft) => void;
}

/** Which group a pending deletion is in, and which card of it. */
interface PendingDelete {
    group: CardGroup;
    index: number;
}

/** The two card lists, named by the draft field that holds each. */
type CardGroup = 'features' | 'services';

const replaceAt = <T,>(list: T[], index: number, value: T): T[] =>
    list.map((item, position) => (position === index ? value : item));

const removeAt = <T,>(list: T[], index: number): T[] =>
    list.filter((_, position) => position !== index);

/** Returns the list unchanged when the move would fall off either end. */
const moveBy = <T,>(list: T[], index: number, offset: number): T[] => {
    const target: number = index + offset;

    if (target < 0 || target >= list.length) {
        return list;
    }

    const moved: T[] = [...list];
    [moved[index], moved[target]] = [moved[target], moved[index]];

    return moved;
};

/**
 * The whole About page as a form.
 *
 * Holds no copy of the draft: every keystroke goes up to the page, which owns it and the
 * single Save that sends it. Order within a group is position in the list, so the arrows
 * are the only reordering there is.
 *
 * Laid out as one card per block of the page, in the order the page reads them, so an
 * admin looking for the mission paragraph finds it where they just saw it.
 */
export const AboutEditor = (props: IAboutEditorProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

    const draft: ProfileDraft = props.draft;

    const update = (changes: Partial<ProfileDraft>): void => props.onChange({ ...draft, ...changes });

    const setCards = (group: CardGroup, next: ProfileCardDraft[]): void => update({ [group]: next });

    const updateCard = (group: CardGroup, index: number, changes: Partial<ProfileCardDraft>): void =>
        setCards(group, replaceAt(draft[group], index, { ...draft[group][index], ...changes }));

    const confirmDelete = (): void => {
        if (!pendingDelete) {
            return;
        }

        const { group, index } = pendingDelete;
        setPendingDelete(null);
        setCards(group, removeAt(draft[group], index));
    };

    /**
     * The Romanian/English pair every piece of text has. Only the Romanian side can be
     * wrong, and only on a card: English is optional and falls back to it, and every field
     * outside a card is optional too -- a clinic that leaves its mission blank has
     * published a shorter page, not an invalid one.
     */
    const languageFields = (
        label: string,
        valueRo: string,
        valueEn: string,
        onChangeRo: (value: string) => void,
        onChangeEn: (value: string) => void,
        options?: { isInvalid?: boolean; rows?: number }
    ): JSX.Element => {
        const isInvalid: boolean = options?.isInvalid ?? false;
        const isMultiline: boolean = (options?.rows ?? 1) > 1;

        return (
            <Box sx={fieldPairStyles}>
                <TextField
                    size="small"
                    sx={textFieldStyles}
                    multiline={isMultiline}
                    minRows={options?.rows}
                    label={`${translate(label)} — ${translate(ResourcesIds.ABOUT_FIELD_RO)}`}
                    value={valueRo}
                    onChange={event => onChangeRo(event.target.value)}
                    error={isInvalid}
                    helperText={isInvalid ? translate(ResourcesIds.ABOUT_REQUIRED_ERROR) : ' '}
                />

                <TextField
                    size="small"
                    sx={textFieldStyles}
                    multiline={isMultiline}
                    minRows={options?.rows}
                    label={`${translate(label)} — ${translate(ResourcesIds.ABOUT_FIELD_EN)}`}
                    value={valueEn}
                    onChange={event => onChangeEn(event.target.value)}
                    helperText=" "
                />
            </Box>
        );
    };

    /** The up/down/delete cluster every card carries. */
    const rowActions = (group: CardGroup, index: number, count: number): JSX.Element => (
        <Box sx={rowActionsStyles}>
            <Tooltip title={translate(ResourcesIds.ABOUT_MOVE_UP)}>
                <span>
                    <IconButton
                        size="small"
                        disabled={index === 0}
                        onClick={() => setCards(group, moveBy(draft[group], index, -1))}
                        aria-label={translate(ResourcesIds.ABOUT_MOVE_UP)}
                    >
                        <ArrowUpward fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={translate(ResourcesIds.ABOUT_MOVE_DOWN)}>
                <span>
                    <IconButton
                        size="small"
                        disabled={index === count - 1}
                        onClick={() => setCards(group, moveBy(draft[group], index, 1))}
                        aria-label={translate(ResourcesIds.ABOUT_MOVE_DOWN)}
                    >
                        <ArrowDownward fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={translate(ResourcesIds.ABOUT_DELETE_CARD)}>
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => setPendingDelete({ group, index })}
                    aria-label={translate(ResourcesIds.ABOUT_DELETE_CARD)}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );

    /**
     * The icon choices, as tiles. A fixed set drawn as pictures rather than a dropdown of
     * names, because the admin is choosing a symbol and a list of names would make them
     * guess at each one.
     */
    const iconPicker = (index: number, selected: string): JSX.Element => (
        <>
            <Typography sx={cardLabelStyles}>{translate(ResourcesIds.ABOUT_CARD_ICON)}</Typography>

            <Box sx={iconPickerStyles}>
                {PROFILE_ICON_NAMES.map(name => (
                    <IconButton
                        key={name}
                        size="small"
                        aria-label={name}
                        aria-pressed={name === selected}
                        sx={iconChoiceStyles(name === selected)}
                        onClick={() => updateCard('features', index, { icon: name })}
                    >
                        {profileIconTile(name)}
                    </IconButton>
                ))}
            </Box>
        </>
    );

    /** One group of cards, with its heading fields, its list and its Add button. */
    const cardBlock = (
        group: CardGroup,
        blockTitle: string,
        headingLabel: string,
        headingRo: string,
        headingEn: string,
        onHeadingRo: (value: string) => void,
        onHeadingEn: (value: string) => void,
        emptyText: string,
        addText: string,
        makeCard: () => ProfileCardDraft
    ): JSX.Element => {
        const cards: ProfileCardDraft[] = draft[group];

        return (
            <Paper elevation={0} sx={blockCardStyles}>
                <Typography sx={blockTitleStyles}>{translate(blockTitle)}</Typography>

                {languageFields(headingLabel, headingRo, headingEn, onHeadingRo, onHeadingEn)}

                {cards.length === 0 && (
                    <Typography variant="body2" sx={emptyBlockStyles}>
                        {translate(emptyText)}
                    </Typography>
                )}

                {cards.map((card, index) => (
                    <Paper key={card.key} elevation={0} sx={cardRowStyles}>
                        <Box sx={cardHeaderStyles}>
                            <Typography sx={cardLabelStyles}>
                                {`${translate(ResourcesIds.ABOUT_CARD_LABEL)} ${index + 1}`}
                            </Typography>

                            {rowActions(group, index, cards.length)}
                        </Box>

                        {group === 'features' && iconPicker(index, card.icon)}

                        {languageFields(
                            ResourcesIds.ABOUT_CARD_TITLE,
                            card.titleRo,
                            card.titleEn,
                            value => updateCard(group, index, { titleRo: value }),
                            value => updateCard(group, index, { titleEn: value }),
                            // The card is flagged as a whole, so each box works out for
                            // itself whether it is the one at fault -- otherwise a missing
                            // body would redden the title too.
                            { isInvalid: props.invalidKeys.has(card.key) && card.titleRo.trim().length === 0 }
                        )}

                        {languageFields(
                            ResourcesIds.ABOUT_CARD_BODY,
                            card.bodyRo,
                            card.bodyEn,
                            value => updateCard(group, index, { bodyRo: value }),
                            value => updateCard(group, index, { bodyEn: value }),
                            {
                                isInvalid: props.invalidKeys.has(card.key) && card.bodyRo.trim().length === 0,
                                rows: 3,
                            }
                        )}
                    </Paper>
                ))}

                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    sx={addButtonStyles}
                    onClick={() => setCards(group, [...cards, makeCard()])}
                >
                    {translate(addText)}
                </Button>
            </Paper>
        );
    };

    return (
        <Box sx={editorWrapperStyles}>
            <Paper elevation={0} sx={blockCardStyles}>
                <Typography sx={blockTitleStyles}>
                    {translate(ResourcesIds.ABOUT_SECTION_INTRO)}
                </Typography>

                {languageFields(
                    ResourcesIds.ABOUT_FIELD_TITLE,
                    draft.aboutTitleRo,
                    draft.aboutTitleEn,
                    value => update({ aboutTitleRo: value }),
                    value => update({ aboutTitleEn: value })
                )}

                {languageFields(
                    ResourcesIds.ABOUT_FIELD_SUBTITLE,
                    draft.aboutSubtitleRo,
                    draft.aboutSubtitleEn,
                    value => update({ aboutSubtitleRo: value }),
                    value => update({ aboutSubtitleEn: value }),
                    { rows: 2 }
                )}
            </Paper>

            <Paper elevation={0} sx={blockCardStyles}>
                <Typography sx={blockTitleStyles}>
                    {translate(ResourcesIds.ABOUT_SECTION_MISSION)}
                </Typography>

                {languageFields(
                    ResourcesIds.ABOUT_FIELD_MISSION_TITLE,
                    draft.missionTitleRo,
                    draft.missionTitleEn,
                    value => update({ missionTitleRo: value }),
                    value => update({ missionTitleEn: value })
                )}

                {languageFields(
                    ResourcesIds.ABOUT_FIELD_MISSION_BODY,
                    draft.missionBodyRo,
                    draft.missionBodyEn,
                    value => update({ missionBodyRo: value }),
                    value => update({ missionBodyEn: value }),
                    { rows: 4 }
                )}
            </Paper>

            {cardBlock(
                'features',
                ResourcesIds.ABOUT_SECTION_FEATURES,
                ResourcesIds.ABOUT_FIELD_FEATURES_TITLE,
                draft.featuresTitleRo,
                draft.featuresTitleEn,
                value => update({ featuresTitleRo: value }),
                value => update({ featuresTitleEn: value }),
                ResourcesIds.ABOUT_NO_FEATURES,
                ResourcesIds.ABOUT_ADD_FEATURE,
                emptyFeatureCard
            )}

            {cardBlock(
                'services',
                ResourcesIds.ABOUT_SECTION_SERVICES,
                ResourcesIds.ABOUT_FIELD_SERVICES_TITLE,
                draft.servicesTitleRo,
                draft.servicesTitleEn,
                value => update({ servicesTitleRo: value }),
                value => update({ servicesTitleEn: value }),
                ResourcesIds.ABOUT_NO_SERVICES,
                ResourcesIds.ABOUT_ADD_SERVICE,
                emptyServiceCard
            )}

            <Paper elevation={0} sx={blockCardStyles}>
                <Typography sx={blockTitleStyles}>
                    {translate(ResourcesIds.ABOUT_SECTION_CTA)}
                </Typography>

                {languageFields(
                    ResourcesIds.ABOUT_FIELD_CTA_TITLE,
                    draft.ctaTitleRo,
                    draft.ctaTitleEn,
                    value => update({ ctaTitleRo: value }),
                    value => update({ ctaTitleEn: value })
                )}

                {languageFields(
                    ResourcesIds.ABOUT_FIELD_CTA_SUBTITLE,
                    draft.ctaSubtitleRo,
                    draft.ctaSubtitleEn,
                    value => update({ ctaSubtitleRo: value }),
                    value => update({ ctaSubtitleEn: value }),
                    { rows: 2 }
                )}

                {languageFields(
                    ResourcesIds.ABOUT_FIELD_CTA_CONTACT,
                    draft.ctaContactRo,
                    draft.ctaContactEn,
                    value => update({ ctaContactRo: value }),
                    value => update({ ctaContactEn: value })
                )}
            </Paper>

            <ConfirmationDialog
                isOpen={pendingDelete !== null}
                onClose={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
                title={translate(ResourcesIds.ABOUT_DELETE_TITLE)}
                description={translate(ResourcesIds.ABOUT_DELETE_DESCRIPTION)}
                confirmMessage={translate(ResourcesIds.ABOUT_DELETE_CARD)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
            />
        </Box>
    );
};
